import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box } from '@material-ui/core';
import AnalyticsLayout from 'components/layout/AnalyticsLayout';
import { setPage } from 'redux/actions/pagesActions';
import Header from 'components/layout/Partials/Header';
import { Helmet } from 'react-helmet';
import { defaultStyles } from 'utils/Theme';
import Loading from 'components/common/Loading';
import { makeQsOptions, getAnonymousIdentity } from 'utils/Helper';
import Page from './Page';
import config from 'coeus.config';
import api from 'api';
import { showSnackbar } from 'redux/alerts/actions';
import { addPage } from 'redux/active/actions';
import { pageHistorySelector } from 'redux/active/selectors';

const VISUALISATION_ERROR_LIST = ['map'];

const Analytics = () => {
    const [qsOptions, setQsOptions] = useState(null);
    const [currentPage, setCurrentPage] = useState(null);
    const classes = defaultStyles();
    const { id } = useParams();
    const dispatch = useDispatch();
    const pageHistory = useSelector(pageHistorySelector);
    const { page } = useSelector((state) => state.pages);
    const { publicAccess } = useSelector((state) => state.settings);
    const { qsServer } = useSelector((state) => state.qsServer);
    const { isAuthenticated, user } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(true);

    const identity = isAuthenticated ? user._id : getAnonymousIdentity();

    const qsServerSetup = (_page) => {
        const qsServerOptions = makeQsOptions(
            { ...qsServer, identity },
            isAuthenticated,
            publicAccess
        );

        const config = {
            ...qsServerOptions,
            config: {
                ...qsServerOptions.config,
                appId: _page.qsApp,
            },
        };
        setQsOptions(config);
    };

    const getCurrentDynamicPageDetails = () => {
        const pageDetails = { ...page.page };
        if (page?.dynamicApp) {
            pageDetails.qsApp = page?.dynamicApp?.qsApp;
        }
        return pageDetails;
    };

    const pageSetup = () => {
        const _page = getCurrentDynamicPageDetails();
        qsServerSetup(_page);
        setCurrentPage(_page);
        setIsLoading(false);
    };

    useEffect(() => {
        if (page) {
            pageSetup();
        }
    }, [page]);

    const loadPage = (pageFunc) => {
        setIsLoading(true);
        pageFunc(id)
            .then((results) => {
                dispatch(setPage(results));
                dispatch(addPage(results.page.id));
            })
            .catch((err) => {
                setIsLoading(false);
                console.error(err);
                dispatch(
                    showSnackbar({
                        severity: 'Error',
                        message: 'Error: failed to open Page',
                    })
                );
            });
    };

    useEffect(() => {
        if (id && qsServer) {
            const pageFunc =
                config.feature.public_access.enabled && !isAuthenticated
                    ? api.pages.getPublicPage
                    : api.pages.getDynamicPage;
            loadPage(pageFunc);
        }
    }, [id, qsServer]);

    const getHelmet = () => {
        const title = page?.title ?? config?.title;
        return (
            <Helmet>
                <title>{title}</title>
            </Helmet>
        );
    };

    if (isLoading) {
        return <Loading />;
    }

    return (
        <AnalyticsLayout>
            {getHelmet()}
            {currentPage && qsOptions && (
                <>
                    <Header
                        title={currentPage?.title}
                        classes={classes}
                        qsOptions={qsOptions}
                        page={currentPage}
                    />
                    <Box>
                        <Page page={currentPage} qsOptions={qsOptions} />
                    </Box>
                </>
            )}
        </AnalyticsLayout>
    );
};

export default Analytics;
